import { ProductVisibility, ReleasePolicy } from 'src/shared/types/engine/products';
import { ProductAccess } from 'src/shared/types/productLicensing';

type ProductAccessInputs = {
    /** How the product's results reach the customer (`PRODUCT_RELEASE`). */
    release: ReleasePolicy;
    /** The product's setting in product licensing management. `undefined` is the default. */
    setting: ProductVisibility | undefined;
    isInternal: boolean;
    /** Whether there are results to show. The caller knows this, not product licensing. */
    hasResults: boolean;
};

/**
 * How much of a product the current user may see, for every product.
 *
 * Two questions decide it. Are the results released to this user? And are
 * there results? A manual product is released when it is set to Visible; an
 * automatic product is always released. Internal staff see every released and
 * unreleased result, so they can check results before the customer does.
 * Hidden withholds the product from external users only.
 */
export const productAccess = ({ release, setting, isInternal, hasResults }: ProductAccessInputs): ProductAccess => {
    const shouldReleaseWhenResultsAvailable = release === ReleasePolicy.AUTOMATIC || setting === ProductVisibility.VISIBLE || isInternal;

    if (setting === ProductVisibility.HIDDEN && !isInternal) {
        return ProductAccess.HIDDEN;
    } else if (shouldReleaseWhenResultsAvailable && hasResults) {
        return ProductAccess.LICENSED;
    } else {
        return ProductAccess.DEFAULT;
    }
};

/**
 * Whether a surface outside a `ProductGate` leaves the product out: it is
 * hidden from the user, or the access is still loading. `ProductGate` shows a
 * loader while it loads; a menu item, card or section has nothing to show
 * instead, so it waits out of sight.
 */
export const isWithheld = (access: ProductAccess): boolean => access === ProductAccess.HIDDEN || access === ProductAccess.LOADING;
